// React Hooks
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from 'react-router-dom';

// Import esterni
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faChevronDown } from '@fortawesome/free-solid-svg-icons';

export interface MenuItem {
    label: string;
    url: string;
    title: string;
    class?: string;
    dropdown?: boolean;
    items?: MenuItem[];
}

export interface AgencyConfig {
    agency: {
        customPhone: string;
        customMobile: string;
    };
    socialLinks: {
        whatsapp: string;
        facebook: string | boolean;
        instagram: string | boolean;
        youtube?: string | boolean;
        linkedin: string | boolean;
    };
}

export interface AgencyData {
    agency_name: string;
    phone1: string;
    mobile: string;
    international_prefix: string;
    whatsapp: string | boolean;
    youtube?: string | boolean;
    facebook: string | boolean;
    instagram: string | boolean;
    twitter: string | boolean;
    linkedin: string | boolean;
    pinterest: string | boolean;
    cookie_solution_iubenda: boolean;
}

interface HeaderProps {
    agencyData: AgencyData;
    languageSelector: React.ReactElement;
    assets?: {
        logoPath?: string;
        menuItemsPath?: string;
    };
    className?: string;
    onMenuItemClick?: (item: MenuItem) => void;
}

const Header: React.FC<HeaderProps> = ({
    agencyData,
    languageSelector,
    assets = { logoPath: "/assets/images/logo-gold.png", menuItemsPath: "/locales" },
    className = "",
    onMenuItemClick,
}) => {

    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [menuLoading, setMenuLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const [collapsed, setCollapsed] = useState(false);
    const [dropDownCollapsed, setDropDownCollapsed] = useState(false);

    console.log('[SharedHeader] agencyData', agencyData);

    useEffect(() => {
        const loadMenuItems = async () => {
            const langCode = currentLanguage.split('-')[0];

            if (!langCode || langCode.includes('cimode')) {
                setMenuItems([]);
                setMenuLoading(false);
                return;
            }

            setMenuLoading(true);
            try {
                const response = await fetch(`${assets.menuItemsPath}/${langCode}/menuItems.json`);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setMenuItems(data || []);

            } catch (error) {
                console.error(`Failed to load menu items for language: ${langCode}`, error);
                setMenuItems([]);
            } finally {
                setMenuLoading(false);
            }
        };

        if (currentLanguage) {
            loadMenuItems();
        }

    }, [currentLanguage, assets.menuItemsPath]);

    const handleMenuItemClick = (item: MenuItem) => {
        setDropDownCollapsed(false);
        setCollapsed(false);
        onMenuItemClick?.(item);
    };

    // Aggiunge position absolute in home page
    const [isHomePage, setIsHomePage] = useState(false);
    const location = useLocation();
    useEffect(() => {
        const isDatailPage = location.pathname.startsWith('/it/immobile/') || location.pathname.startsWith('/en/immobile/');
        if (location.pathname === '/it' || location.pathname === '/en' || location.pathname === '/' || isDatailPage) {
            setIsHomePage(true);
            document.querySelector('header')?.classList.add('position-absolute');
        } else {
            setIsHomePage(false);
            document.querySelector('header')?.classList.remove('position-absolute');
        }
    }, [location]);

    // Aggiunge header sticky quando si scrolla
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [headerIsSticky, setHeaderIsSticky] = useState(false);
    const headerTag = useRef<HTMLElement>(null);

    useEffect(() => {

        setWindowWidth(window.innerWidth);
        const handleScroll = () => {
            if (headerTag.current) {
                setHeaderIsSticky(window.scrollY > headerTag.current.offsetTop);
            }
        };
        window.addEventListener('scroll', handleScroll);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
          
    }, [windowWidth]);


    return (
        <header className={`header ${className} ${isHomePage ? 'p-absolute' : ''} ${headerIsSticky ? 'sticky' : ''}`} ref={headerTag}>
            <nav className="navbar navbar-static-top navbar-expand-lg header-sticky">
                <div className="container-fluid px-3">
                    {/* Bottone Hamburger */}
                    <button
                        type="button"
                        className={`navbar-toggler ${!collapsed ? "" : "collapsed"}`}
                        aria-controls="navbarCollapse"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                        onClick={() => { setDropDownCollapsed(false); setCollapsed(prev => !prev); }}
                    >
                        <FontAwesomeIcon icon={faBars} />
                    </button>

                    {/* Logo Navbar */}
                    <Link className="navbar-brand d-lg-none" to="/" title={t('navigation.homeLinkTitle') as string}>
                        <img
                            className="img-fluid"
                            src={assets.logoPath}
                            alt={`${t('header.navbar.logoAlt')}`}
                        />
                    </Link>

                    {/* Menu Collapse */}
                    <div className={`navbar-collapse collapse pr-xl-5 mr-xl-5 ${!collapsed ? "" : "show"}`} id="navbarCollapse">
                        {menuLoading ? (
                            <div className="navbar-nav"><span className="nav-link">{t('common.loading')}...</span></div>
                        ) : (
                            <>
                                {/* Logo Navbar */}
                                <Link className="navbar-brand d-none d-lg-block" to="/" title={t('navigation.homeLinkTitle') as string}>
                                    <img
                                        className="img-fluid"
                                        src={assets.logoPath}
                                        alt={`${t('header.navbar.logoAlt')}`}
                                    />
                                </Link>
                                <ul className="nav navbar-nav">
                                    {menuItems.map((menuItem, index) => (
                                        <li key={index} className={`nav-item ${menuItem.dropdown ? 'dropdown' : ''}`}>
                                            {menuItem.url && menuItem.url !== '#' && !menuItem.dropdown ? (
                                                <Link
                                                    to={menuItem.url}
                                                    title={menuItem.title}
                                                    className={`nav-link ${menuItem.class || ''}`}
                                                    onClick={() => handleMenuItemClick(menuItem)}
                                                >
                                                    <span>{menuItem.label}</span>
                                                    {menuItem.class && (
                                                        <span>{menuItem.label}</span>
                                                    )}
                                                </Link>
                                            ) : (
                                                <a
                                                    href={menuItem.url || '#'}
                                                    title={menuItem.title}
                                                    className={`nav-link ${menuItem.dropdown ? 'dropdown-toggle' : ''} ${menuItem.class || ''}`}
                                                    role={menuItem.dropdown ? 'button' : undefined}
                                                    aria-haspopup={menuItem.dropdown ? true : undefined}
                                                    aria-expanded={menuItem.dropdown ? false : undefined}
                                                    onClick={(e) => { e.preventDefault(); setDropDownCollapsed(prev => !prev) }}
                                                >
                                                    {menuItem.label}
                                                    {menuItem.dropdown && (
                                                        <span className="dropdownchevron ml-1">
                                                            <FontAwesomeIcon icon={faChevronDown} size="xs" />
                                                        </span>
                                                    )}
                                                </a>
                                            )}
                                            {/* Dropdown Submenu */}
                                            {menuItem.dropdown && menuItem.items && (
                                                <ul className={`dropdown-menu ${!dropDownCollapsed ? "" : "show"}`} aria-labelledby={`navbarDropdown${index}`}>
                                                    {menuItem.items.map((item, subIndex) => (
                                                        <li key={subIndex}>
                                                            {item.url && !item.url.startsWith('http') ? (
                                                                <Link
                                                                    to={item.url}
                                                                    title={item.title}
                                                                    className="dropdown-item"
                                                                    onClick={() => handleMenuItemClick(item)}
                                                                >
                                                                    {item.label}
                                                                </Link>
                                                            ) : (
                                                                <a
                                                                    href={item.url || '#'}
                                                                    title={item.title}
                                                                    className="dropdown-item"
                                                                    target={item.url && item.url.startsWith('http') ? '_blank' : undefined}
                                                                    rel={item.url && item.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                                    onClick={() => handleMenuItemClick(item)}
                                                                >
                                                                    {item.label}
                                                                </a>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                {/* Language Selector */}
                                {languageSelector}
                            </>
                        )}
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;
